import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import UsagePill from "@/components/UsagePill";

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-hidden grain-bg" data-testid="landing-page">
      {/* header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-zinc-200/60"
        data-testid="landing-header"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#FF4757] flex items-center justify-center text-white font-bold text-sm">V</span>
            <span className="font-bold tracking-tight text-lg">velo2one</span>
          </div>
          <div className="flex items-center gap-3">
            <UsagePill className="hidden sm:inline-flex" />
            <Link
              to="/analyze"
              className="btn-primary rounded-full px-5 py-2 text-sm font-semibold"
              data-testid="header-cta-btn"
            >
              Analyze my idea
            </Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 pt-20 md:pt-28 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-[#FFF4E5] text-[#B27300] px-3 py-1 text-xs font-semibold mb-6"
              data-testid="landing-tagline-badge"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Zero to one, minus the guesswork
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-zinc-900">
              Check your idea&rsquo;s <span className="text-[#FF4757]">strength</span>, then get everything you need to take it to market.
            </h1>
            <p className="mt-6 text-lg text-zinc-700 max-w-xl leading-relaxed">
              velo2one gives you an honest read on how strong your idea looks, then hands you everything you
              need to move forward regardless &mdash; ICP, target market, pricing, competition.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/analyze"
                className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
                data-testid="hero-start-btn"
              >
                Get my full plan
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how-it-works"
                className="rounded-full px-6 py-3 text-sm font-semibold border border-zinc-300 hover:border-zinc-900 transition-colors"
                data-testid="hero-how-btn"
              >
                How it works
              </a>
            </div>
            <p className="mt-4 text-xs text-zinc-500">No signup. No download. Runs in ~30 seconds.</p>
          </div>

          <div className="md:col-span-5">
            <div className="velo-card p-6 md:p-8" data-testid="landing-preview-card">
              <p className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-4">Live sample &mdash; idea strength signal</p>
              <div className="space-y-3">
                <PreviewRow color="green" label="Problem realness" text="Real, recurring pain — 200k+ threads discussing it." />
                <PreviewRow color="amber" label="Monetization signal" text="Buyers pay adjacent tools, not this exact one yet — worth pressure-testing." />
                <PreviewRow color="red" label="Differentiation" text="Wedge overlaps with incumbent's recent launch — sharper positioning needed." />
              </div>
              <div className="mt-6 border-t border-zinc-200 pt-4">
                <p className="text-sm text-zinc-700 leading-relaxed">
                  <span className="font-semibold">And regardless:</span> you still get the full ICP, market,
                  pricing, and competition plan &mdash; framed as &ldquo;here&rsquo;s how to move forward anyway&rdquo;.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* about */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 pb-8" data-testid="landing-about">
        <div className="velo-card p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <p className="text-xs font-semibold tracking-widest text-zinc-500 uppercase mb-2">About velo2one</p>
            <p className="font-serif-display text-3xl md:text-4xl leading-tight text-zinc-900">
              Zero to one, <span className="text-[#FF4757]">minus the guesswork</span>.
            </p>
          </div>
          <div className="md:col-span-8">
            <p className="text-base md:text-lg text-zinc-700 leading-relaxed">
              Think of it like checking if a domain name is available &mdash; velo2one gives you an honest read on
              how strong your idea looks, then hands you everything you need to move forward regardless. Your ideal
              customer, the right market to target, a defensible pricing range, and a clear-eyed look at your
              competition &mdash; all in minutes, so you go from idea to go-to-market with real evidence behind
              you, not just conviction.
            </p>
          </div>
        </div>
      </section>

      {/* how it works */}
      <section id="how-it-works" className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 pb-24">
        <h2 className="font-serif-display text-4xl md:text-5xl text-zinc-900 mb-10">One signal. Four plans. Zero gatekeeping.</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { n: "01", t: "Strength Signal", d: "Green/amber/red on realness, monetization, differentiation. Informative, never blocking." },
            { n: "02", t: "ICP", d: "Persona or buying committee tuned to B2C, B2B, or B2G." },
            { n: "03", t: "Market", d: "5-factor readiness read for the countries that matter." },
            { n: "04", t: "Pricing", d: "Ranges + model, benchmarked to your market and wedge." },
            { n: "05", t: "Competition", d: "Direct, indirect, gaps — and your defensible wedge." },
          ].map((s) => (
            <div key={s.n} className="velo-card p-6" data-testid={`step-card-${s.n}`}>
              <p className="text-xs font-mono text-[#FF4757] mb-2">{s.n}</p>
              <h3 className="font-bold text-base mb-1">{s.t}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-start gap-4">
          <Link
            to="/analyze"
            className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
            data-testid="bottom-cta-btn"
          >
            Get my full plan
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-zinc-500">
            Every founder gets the full ICP, market, pricing, and competitive analysis &mdash; every time.
          </p>
        </div>
      </section>
    </div>
  );
}

function PreviewRow({ color, label, text }) {
  const cls = { green: "label-green", amber: "label-amber", red: "label-red" }[color];
  return (
    <div className="flex items-start gap-3">
      <span className={`${cls} rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap uppercase tracking-wider`}>
        {color}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-zinc-900">{label}</p>
        <p className="text-xs text-zinc-600 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}
